import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import withWidth from '@material-ui/core/withWidth';

import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import Filter from 'components/common/Filter';
import InventoryNavigation from 'components/inventory/InventoryNavigation';
import InventoryWarehousesTable from 'components/settings/InventoryWarehousesTable';
import InventoryWarehouseCreateEditDialog from 'components/settings/InventoryWarehouseCreateEditDialog';

import {
  fetchInventoryWarehouses,
  clearInventoryWarehouses,
} from 'redux/modules/inventoryWarehouses';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

class Warehouses extends Component {
  constructor(props) {
    super(props);

    this.state = {
      createDialogOpen: false,
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
      limit: 10,
      offset: 0,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onFilterButtonClick = this.onFilterButtonClick.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearInventoryWarehouses();
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters, width } = this.props;
    const { limit, offset } = this.state;
    const filtersChanged = nextProps.filters.Location !== filters.Location;
    let newOffset = offset;
    if (filtersChanged) {
      newOffset = 0;
    }

    if (newOffset !== offset) {
      this.setState({ offset: newOffset });
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters, inventoryWarehouses, count, loading } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['Location'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    } else if (inventoryWarehouses.length < count
      && this.scrollRef
      && this.scrollRef.scrollTop === 0
      && loading === 0) {
      this.onTableScroll({ target: this.scrollRef });
    }
  }

  onRequest() {
    const { actions, filters } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        name: key,
        operation: 'contains',
        val: filters[key],
        case_sensitivity: false,
      }))
      : null;
    actions.fetchInventoryWarehouses({
      limit,
      offset,
      filters: requestFilters,
      triggerLoading: true,
      push: offset > 0,
    });
  }

  onFilterButtonClick() {
    this.setState({ createDialogOpen: true });
  }

  onCreateDialogClose() {
    this.setState({ createDialogOpen: false });
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  onTableScroll(event) {
    const { offset, limit } = this.state;

    if ((event.target.offsetHeight + event.target.scrollTop) / event.target.scrollHeight > 0.9) {
      if ((this.props.inventoryWarehouses.length - limit) >= offset) {
        this.setState({
          offset: offset + limit,
        });
      }
    }
  }

  render() {
    const { inventoryWarehouses, count, loading, location, filters } = this.props;
    const { createDialogOpen, mobileFilterExpanded } = this.state;
    return (
      <Layout>
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Inventory Warehouse Locations
          </title>
        </Helmet>
        <InventoryNavigation location={location} />
        <Filter
          form="inventoryWarehousesFilter"
          placeholder="Search Inventory Warehouses"
          primaryButtonText="Add New"
          onPrimaryButtonClick={this.onFilterButtonClick}
          beforeTable
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          mobileFilterExpanded={mobileFilterExpanded}
          filters={filters}
          showTableCount
          count={count}
          loading={loading}
          items={inventoryWarehouses}
        />
        {loading > 0 &&
          <Progress fixed />
        }
        <InventoryWarehousesTable
          items={inventoryWarehouses}
          count={count}
          loading={loading}
          mobileFilterExpanded={mobileFilterExpanded}
          onScroll={this.onTableScroll}
          scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
        />
        <InventoryWarehouseCreateEditDialog
          form="inventoryWarehouseCreate"
          open={createDialogOpen}
          onClose={this.onCreateDialogClose}
          onSubmitSuccess={() => {
            this.onCreateDialogClose();
            this.onRequest();
          }}
        />
      </Layout>
    );
  }
}

Warehouses.propTypes = {
  inventoryWarehouses: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    Location: PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchInventoryWarehouses: PropTypes.func.isRequired,
    clearInventoryWarehouses: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  width: PropTypes.string.isRequired,
};

const mapStateToProps = (store) => {
  const inventoryWarehousesFilterValues = getFormValues('inventoryWarehousesFilter')(store);
  return {
    inventoryWarehouses: store.inventoryWarehouses.data,
    count: store.inventoryWarehouses.count,
    filters: {
      ...inventoryWarehousesFilterValues
        && inventoryWarehousesFilterValues.Filter
        ? { Location: inventoryWarehousesFilterValues.Filter }
        : {},
    },
    loading: store.inventoryWarehouses.loading,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchInventoryWarehouses,
    clearInventoryWarehouses,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withWidth()(Warehouses));



// WEBPACK FOOTER //
// ./src/pages/inventory/Warehouses.js